<template>
    <div class="page-container">

        <div class="legal-page">
            <router-link to="/about" class="back-link">
                <ArrowLeft :size="20" />
                <span>Back to About</span>
            </router-link>

            <h1>Terms of Service</h1>
            <p class="last-updated">Last updated: {{ currentDate }}</p>

            <div class="content">
                <section>
                    <h2>1. Agreement to Terms</h2>
                    <p>By accessing or using Curie ("we," "us," "our," or "the Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access or use the Service.</p>
                    <p>We reserve the right to modify these terms at any time. We will provide at least 14 days' notice of significant changes. Your continued use of the Service after changes take effect constitutes acceptance of the revised terms.</p>
                </section>

                <section>
                    <h2>2. Description of Service</h2>
                    <p>Curie is an educational platform providing HSC Chemistry practice for NSW students. Our Service includes:</p>
                    <ul>
                        <li>Practice questions aligned with the HSC Chemistry syllabus</li>
                        <li>AI-powered feedback and grading</li>
                        <li>Progress tracking and performance analytics</li>
                        <li>Study tools including hints and explanations</li>
                    </ul>
                    <p>Features may vary based on your subscription tier. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time.</p>
                </section>

                <section>
                    <h2>3. User Accounts</h2>

                    <h3>3.1 Eligibility</h3>
                    <p>To use Curie, you must be at least 13 years old. If you are under 18 years of age, you must have the consent of a parent or legal guardian to create an account and use the Service.</p>

                    <h3>3.2 Account Responsibilities</h3>
                    <p>When you create an account, you agree to:</p>
                    <ul>
                        <li>Provide accurate and complete information</li>
                        <li>Maintain the security of your account credentials</li>
                        <li>Not share your account with others</li>
                        <li>Notify us immediately of any unauthorised access</li>
                        <li>Accept responsibility for all activities under your account</li>
                    </ul>

                    <h3>3.3 Account Termination</h3>
                    <p>We may suspend or terminate your account if you violate these terms, engage in fraudulent activity, or for any other reason at our discretion. You may delete your account at any time through your account settings.</p>
                </section>

                <section>
                    <h2>4. Subscription Tiers</h2>
                    <p>Curie offers different access levels:</p>
                    <ul>
                        <li><strong>Guest:</strong> Limited questions per day without registration</li>
                        <li><strong>Free:</strong> Registered users with daily question limits</li>
                        <li><strong>Student:</strong> Unlimited access to all questions and basic analytics</li>
                        <li><strong>Pro:</strong> Unlimited access to all questions, detailed analytics and question filtering</li>
                    </ul>
                    <p>Specific features and limits for each tier are detailed on our pricing page and may change over time.</p>
                </section>

                <section>
                    <h2>5. Payment and Billing</h2>

                    <h3>5.1 Payment Processing</h3>
                    <p>All payments are processed securely through Stripe. By making a purchase, you agree to Stripe's terms of service. We do not store your full payment card details on our servers.</p>

                    <h3>5.2 Subscription Terms</h3>
                    <ul>
                        <li>Subscriptions automatically renew at the end of each billing period unless cancelled</li>
                        <li>You may cancel your subscription at any time through your account settings</li>
                        <li>Cancellation takes effect at the end of the current billing period</li>
                        <li>No partial refunds are provided for unused portions of a billing period</li>
                    </ul>

                    <h3>5.3 Price Changes</h3>
                    <p>We may change subscription prices with at least 14 days' advance notice. Price changes will apply to subsequent billing periods, not current subscriptions.</p>

                    <h3>5.4 Refunds</h3>
                    <p>Refund requests are handled on a case-by-case basis. If you believe you are entitled to a refund, please contact us at <a :href="'mailto:' + config.contact.email">{{ config.contact.email }}</a> within 7 days of your last purchase.</p>
                </section>

                <section>
                    <h2>6. Intellectual Property</h2>

                    <h3>6.1 Our Content</h3>
                    <p>All questions, educational materials, graphics, logos, and software on Curie are owned by us or our licensors and are protected by copyright and intellectual property laws. You may not:</p>
                    <ul>
                        <li>Copy, reproduce, or distribute our content without permission</li>
                        <li>Create derivative works based on our materials</li>
                        <li>Remove any copyright or proprietary notices</li>
                        <li>Use our content for commercial purposes</li>
                    </ul>

                    <h3>6.2 Limited License</h3>
                    <p>We grant you a limited, non-exclusive, non-transferable license to access and use the Service for your personal, non-commercial educational purposes only.</p>

                    <h3>6.3 Your Content</h3>
                    <p>You retain ownership of answers and other content you submit. By submitting content, you grant us a non-exclusive license to use, analyse, and process your submissions for:</p>
                    <ul>
                        <li>Providing feedback and grading</li>
                        <li>Improving our AI systems and question quality</li>
                        <li>Generating anonymised analytics and statistics</li>
                    </ul>
                </section>

                <section>
                    <h2>7. Acceptable Use</h2>
                    <p>When using Curie, you agree not to:</p>
                    <ul>
                        <li>Share your account credentials with others</li>
                        <li>Attempt to bypass subscription limits or access controls</li>
                        <li>Use automated tools, bots, or scripts to access the Service</li>
                        <li>Interfere with or disrupt the Service or servers</li>
                        <li>Attempt to reverse engineer or decompile any part of the Service</li>
                        <li>Use the Service for any unlawful purpose</li>
                    </ul>
                </section>

                <section>
                    <h2>8. Academic Integrity</h2>
                    <p>Curie is designed as a study and practice tool. You agree to use the Service in accordance with your school's academic integrity policies. Specifically, you must not:</p>
                    <ul>
                        <li>Use Curie during examinations or assessments where it is prohibited</li>
                        <li>Share questions or answers during supervised assessments</li>
                        <li>Submit our content as your own work in graded assignments without proper attribution</li>
                        <li>Distribute questions to study groups or tutoring services</li>
                    </ul>
                    <p>Violations of academic integrity may result in account suspension or termination.</p>
                </section>

                <section>
                    <h2>9. Content Accuracy</h2>
                    <p>While we strive for accuracy, Curie is an educational tool and may contain errors. Our content:</p>
                    <ul>
                        <li>Is provided for educational purposes only</li>
                        <li>Should not be considered as professional advice</li>
                        <li>May not reflect the most current syllabus changes immediately</li>
                    </ul>
                    <p>We encourage you to verify important information with your teachers or official NESA resources.</p>
                </section>

                <section>
                    <h2>10. Limitation of Liability</h2>
                    <p>To the maximum extent permitted by Australian law:</p>
                    <ul>
                        <li>The Service is provided "as is" without warranties of any kind, express or implied</li>
                        <li>We do not guarantee that the Service will be uninterrupted, error-free, or secure</li>
                        <li>We are not liable for any indirect, incidental, special, or consequential damages</li>
                        <li>Our total liability shall not exceed the amount you paid us in the 12 months prior to the claim</li>
                    </ul>
                    <p>Nothing in these terms excludes or limits our liability for matters that cannot be excluded under Australian Consumer Law.</p>
                </section>

                <section>
                    <h2>11. Indemnification</h2>
                    <p>You agree to indemnify and hold harmless Curie and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:</p>
                    <ul>
                        <li>Your use of the Service</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any third-party rights</li>
                    </ul>
                </section>

                <section>
                    <h2>12. Governing Law</h2>
                    <p>These Terms are governed by the laws of New South Wales, Australia. Any disputes shall be subject to the exclusive jurisdiction of the courts of New South Wales.</p>
                </section>

                <section>
                    <h2>13. Severability</h2>
                    <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>
                </section>

                <section>
                    <h2>14. Contact Us</h2>
                    <p>If you have any questions about these Terms of Service, please contact us at:</p>
                    <p><strong>Email:</strong> <a :href="'mailto:' + config.contact.email">{{ config.contact.email }}</a></p>
                    <p><strong>Location:</strong> {{ config.contact.location }}</p>
                </section>
            </div>
        </div>

        <!-- Footer Section -->
        <FooterSection />
    </div>
</template>

<script>
import FooterSection from '@/components/home/FooterSection.vue';
import { ArrowLeft } from 'lucide-vue-next';
import { SITE_CONFIG } from '@/config/site';

export default {
    name: 'TermsView',
    components: {
        FooterSection,
        ArrowLeft
    },
    data() {
        return {
            config: SITE_CONFIG
        };
    },
    computed: {
        currentDate() {
            return new Date().toLocaleDateString('en-AU', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

* {
    font-family: "Space Grotesk", sans-serif;
    font-weight: 400;
    letter-spacing: 1px;
    box-sizing: border-box;
}

.page-container {
    position: relative;
    overflow-x: hidden;
    min-height: 100vh;
    background: var(--bg-primary);
}

.legal-page {
    position: relative;
    z-index: 2;
    max-width: 800px;
    margin: 0 auto;
    padding: 80px 20px;
}

.back-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--accent);
    text-decoration: none !important;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 40px;
    transition: transform 0.2s ease;
}

.back-link:hover {
    transform: translateX(-4px);
}

h1 {
    font-size: 48px;
    color: var(--text-primary);
    margin-bottom: 12px;
}

.last-updated {
    color: var(--text-secondary);
    font-size: 14px;
    margin-bottom: 50px;
}

.content {
    color: var(--text-secondary);
    line-height: 1.8;
}

section {
    margin-bottom: 40px;
}

h2 {
    font-size: 22px;
    color: var(--text-primary);
    margin-bottom: 16px;
    font-weight: 600;
}

h3 {
    font-size: 18px;
    color: var(--text-primary);
    margin-top: 24px;
    margin-bottom: 12px;
    font-weight: 500;
}

p {
    font-size: 16px;
    margin-bottom: 16px;
}

ul {
    margin-left: 24px;
    margin-bottom: 16px;
}

li {
    margin-bottom: 8px;
    font-size: 16px;
}

a {
    color: var(--accent);
    text-decoration: none;
}

a:hover {
    text-decoration: underline;
}

strong {
    color: var(--text-primary);
    font-weight: 600;
}

/* Scrollbar */
::-webkit-scrollbar {
    width: 12px;
}

::-webkit-scrollbar-track {
    background: var(--bg-primary);
}

::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 6px;
}

@media (max-width: 768px) {
    .legal-page {
        padding: 40px 20px;
    }

    h1 {
        font-size: 36px;
    }

    h2 {
        font-size: 20px;
    }

    h3 {
        font-size: 17px;
    }
}
</style>
